import { useEffect, useRef, useState } from 'react'
import { TextInput, View } from 'react-native'

import { inputTextStyle } from '../theme/inputTextStyle'
import { useTheme } from '../theme/restyle'
import type { FieldInputRef } from './FiatFormFieldRow'

const widths = [65, 55, 77]
const lengths = [3, 2, 4]
const split = (value: string) => [
  value.slice(0, 3),
  value.slice(3, 5),
  value.slice(5, 9),
]
/**
 * The single box collects the last four digits, so a pasted full SSN is read
 * from its end; anything shorter is a typed overflow and keeps the first four.
 */
const lastFour = (digits: string) => {
  if (digits.length >= 9) {
    return digits.slice(-4)
  }
  return digits.slice(0, 4)
}

export function FiatSsnField({
  value,
  length,
  label,
  editable,
  autoFocus,
  invalid,
  inputRef,
  onChange,
  onBlur,
}: {
  value: string
  length: 4 | 9
  label: string
  editable: boolean
  autoFocus: boolean
  invalid: boolean
  inputRef?: FieldInputRef
  onChange: (value: string) => void
  onBlur: () => void
}) {
  const theme = useTheme()
  const refs = useRef<Array<TextInput | null>>([])
  const [parts, setParts] = useState(() =>
    length === 4 ? [value] : split(value),
  )
  useEffect(() => {
    if (parts.join('') !== value) {
      setParts(length === 4 ? [value] : split(value))
    }
  }, [value, length])
  const change = (index: number, raw: string) => {
    if (!editable) {
      return
    }
    const digits = raw.replace(/\D/g, '')
    if (digits.length > (length === 4 ? 4 : (lengths[index] ?? 4))) {
      // A paste lands in one box but belongs to the whole number, so it fills
      // from that box on and leaves the segments before it alone.
      const kept = parts.slice(0, index).join('')
      const next =
        length === 4 ? [lastFour(digits)] : split((kept + digits).slice(0, 9))
      setParts(next)
      onChange(next.join(''))
      refs.current[next.length - 1]?.focus()
      return
    }
    const next = parts.map((part, at) => (at === index ? digits : part))
    setParts(next)
    onChange(next.join(''))
    if (digits.length === lengths[index]) {
      refs.current[index + 1]?.focus()
    }
  }
  const fill = invalid ? theme.form.fieldInvalid.fill : theme.form.field
  const color = invalid
    ? theme.form.fieldInvalid.content
    : theme.content.elements.primary
  return (
    <View style={{ flexDirection: 'row', gap: 8 }}>
      {parts.map((part, index) => (
        <View
          key={index}
          style={{
            width: length === 4 ? 77 : widths[index],
            height: 56,
            borderRadius: 16,
            backgroundColor: fill,
            justifyContent: 'center',
            paddingHorizontal: 12,
          }}
        >
          <TextInput
            ref={(node) => {
              refs.current[index] = node
              if (index === 0 && inputRef !== undefined) {
                if (typeof inputRef === 'function') {
                  inputRef(node)
                } else {
                  inputRef.current = node
                }
              }
            }}
            value={part}
            editable={editable}
            autoFocus={autoFocus && index === 0}
            accessibilityLabel={
              length === 4 ? label : `${label}, ${index + 1} / 3`
            }
            placeholder={length === 4 ? '1234' : ['123', '45', '6789'][index]}
            placeholderTextColor={theme.content.elements.tertiary}
            keyboardType="number-pad"
            autoComplete="off"
            autoCorrect={false}
            spellCheck={false}
            selection={{ start: part.length, end: part.length }}
            onChangeText={(text) => change(index, text)}
            onKeyPress={({ nativeEvent }) => {
              if (
                editable &&
                nativeEvent.key === 'Backspace' &&
                part === '' &&
                index > 0
              ) {
                refs.current[index - 1]?.focus()
              }
            }}
            onBlur={onBlur}
            style={[
              inputTextStyle(theme),
              { padding: 0, color, textAlign: 'center' },
            ]}
          />
        </View>
      ))}
    </View>
  )
}
